import { MotionConfig, useReducedMotion, type ViewportOptions } from "framer-motion";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { viewport as defaultViewport } from "./tokens";

interface MotionPrefs {
  reduce: boolean;
  viewport: ViewportOptions;
}

const MotionPrefsContext = createContext<MotionPrefs>({
  reduce: false,
  viewport: defaultViewport,
});

export function MotionProvider({ children }: { children: ReactNode }) {
  // Snapshot at first render — this hook does not live-update despite its JSDoc.
  // Changing the OS setting requires a reload.
  const reduce = useReducedMotion() ?? false;

  const value = useMemo<MotionPrefs>(
    () => ({
      reduce,
      // Under reduced motion, fire as soon as anything is on screen: no
      // anticipatory offset, no waiting for 20% coverage.
      viewport: reduce ? { once: true, amount: "some" } : defaultViewport,
    }),
    [reduce],
  );

  return (
    <MotionPrefsContext.Provider value={value}>
      {/* Engine-level backstop: forces {type:false} on every transform key, so
          transforms snap while opacity still cross-fades. Catches ad-hoc
          motion components that bypass these primitives. */}
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </MotionPrefsContext.Provider>
  );
}

export const useMotionPrefs = () => useContext(MotionPrefsContext);
